import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { getNetwork, updateNetwork } from "./networkApi";

const NetworkSettingsPage = () => {
  const { networkId } = useParams();
  const toast = useToast();
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.600", "gray.400");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", description: "" });

  const loadNetwork = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const network = await getNetwork(networkId);
      setForm({
        name: network?.name || "",
        description: network?.description || "",
      });
    } catch (err) {
      console.error("Error loading network settings:", err);
      setError("Could not load network settings.");
    } finally {
      setLoading(false);
    }
  }, [networkId]);

  useEffect(() => {
    loadNetwork();
  }, [loadNetwork]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await updateNetwork(networkId, form);
      toast({ title: "Network settings saved", status: "success", duration: 3000, isClosable: true });
      await loadNetwork();
    } catch (err) {
      console.error("Error saving network settings:", err);
      toast({ title: "Could not save network settings", status: "error", duration: 4000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <HStack justify="center" py={12}><Spinner size="lg" /></HStack>;
  }

  if (error) {
    return <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>;
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Heading size="lg">Network Settings</Heading>
        <Text color={mutedColor} mt={1}>Edit network name and description.</Text>
      </Box>

      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} />
              </FormControl>
              <HStack justify="flex-end">
                <Button type="submit" colorScheme="blue" variant="outline" isLoading={saving}>
                  Save changes
                </Button>
              </HStack>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default NetworkSettingsPage;
